const ORDER_GENIE_SETTINGS = '/order-x?embed=settings';
const ACCOUNT_ADMIN = '/order-x/account-admin';

function ogSettings(ogTab, extra = {}) {
  const params = new URLSearchParams({ embed: 'settings', ogTab, ...extra });
  return `/order-x?${params.toString()}`;
}

/** @typedef {{ to: string, label: string, instructions: string[] }} ReadinessFixGuide */

/** @type {Record<string, ReadinessFixGuide & { opensearch?: Partial<ReadinessFixGuide> }>} */
const FIX_BY_CHECK_ID = {
  'platform.backend.health': {
    to: ACCOUNT_ADMIN,
    label: 'OrderX Account Admin',
    instructions: [
      'Confirm the backend API is running on port 8000.',
      'If needed, set ai_tech_api_base_url in OrderX settings to match your backend URL.',
    ],
  },
  'platform.order_genie.health': {
    to: ACCOUNT_ADMIN,
    label: 'OrderX Account Admin',
    instructions: [
      'Start OrderX on port 8001 (./start-order-genie.sh).',
      'Set order_genie_service_url to http://localhost:8001 for local dev.',
    ],
  },
  'og.settings.loaded': {
    to: ACCOUNT_ADMIN,
    label: 'OrderX Account Admin',
    instructions: ['Open Account Admin, review settings, and click Save once to refresh cached settings.'],
  },
  'og.service_url.reachable': {
    to: `${ACCOUNT_ADMIN}?section=search`,
    label: 'Account Admin → Product Matching Search',
    instructions: [
      'Verify order_genie_service_url points to a running OrderX instance.',
      'Local dev: http://localhost:8001 — then confirm GET /health returns 200.',
    ],
  },
  'og.proxy.routing': {
    to: `${ACCOUNT_ADMIN}?section=search`,
    label: 'Account Admin → Product Matching Search',
    instructions: [
      'Ensure backend :8000 and OrderX :8001 are both running.',
      'Align order_genie_service_url with the URL the backend proxy uses.',
    ],
  },
  'account.general_config.present': {
    to: '/configurations?tab=general',
    label: 'Configurations → General',
    instructions: ['Create or save the general account configuration for this account.'],
  },
  'account.api_key.valid': {
    to: '/configurations?tab=account',
    label: 'Configurations → Account',
    instructions: ['Confirm the account API key is present and matches what the app sends in X-API-Key.'],
  },
  'ai.engine.selected': {
    to: ogSettings('settings-defaults', { defaultsTab: 'ai_engine' }),
    label: 'OrderX → Settings → Defaults → AI Engine Setup',
    instructions: ['Choose the default AI engine (OpenAI, Gemini, or Anthropic) and save settings.'],
  },
  'ai.key.configured': {
    to: ogSettings('settings-defaults', { defaultsTab: 'ai_engine' }),
    label: 'OrderX → Settings → Defaults → AI Engine Setup',
    instructions: [
      'Open the tab for your selected engine and enter a valid API key.',
      'Save OrderX settings, then re-run readiness.',
    ],
  },
  'ai.agents.quote_reader': {
    to: ogSettings('settings-defaults', { defaultsTab: 'agents' }),
    label: 'OrderX → Settings → Defaults → Agent Assignments',
    instructions: [
      'Select a Quote Reading Agent (or add agents under the Agents tab first).',
      'Save OrderX settings, then re-run readiness.',
    ],
  },
  'ai.agents.match_evaluator': {
    to: ogSettings('settings-defaults', { defaultsTab: 'agents' }),
    label: 'OrderX → Settings → Defaults → Agent Assignments',
    instructions: [
      'Select a Match Evaluation Agent for AI reranking.',
      'Save OrderX settings, then re-run readiness.',
    ],
  },
  'ai.ping': {
    to: ogSettings('settings-defaults', { defaultsTab: 'ai_engine' }),
    label: 'OrderX → Settings → Defaults → AI Engine Setup',
    instructions: [
      'Verify the API key for your default engine is valid and not expired.',
      'Save settings and re-run readiness with include E2E enabled.',
    ],
  },
  'search.engine.configured': {
    to: `${ACCOUNT_ADMIN}?section=search`,
    label: 'Account Admin → Product Matching Search',
    instructions: [
      'Set product_match_engine to solr or opensearch (not shadow).',
      'Save account admin settings.',
    ],
  },
  'search.solr.reachable': {
    to: '/ahri/solr',
    label: 'AHRI → Solr configuration',
    instructions: [
      'Configure Solr base URL for this account.',
      'Confirm Solr admin ping succeeds.',
    ],
  },
  'search.opensearch.cluster': {
    to: '/configurations?tab=ai_search',
    label: 'Configurations → AI Search',
    instructions: [
      'Set OpenSearch Data Indexing URL (use :8443 for managed clusters when needed).',
      'Test connection and save.',
    ],
  },
  'search.opensearch.shop_api': {
    to: '/configurations?tab=ai_search',
    label: 'Configurations → AI Search',
    instructions: [
      'Verify OpenSearch shop/base URL and credentials.',
      'Run test connection from AI Search settings.',
    ],
  },
  'search.collection.exists': {
    to: '/products/ai-search',
    label: 'Products → AI Search Indexing',
    instructions: [
      'Run a full product index import for this account.',
      'Confirm the index/collection name matches products_{account_id}.',
    ],
  },
  'search.doc_count': {
    to: '/products/ai-search',
    label: 'Products → AI Search Indexing',
    instructions: [
      'Run a full product index sync — your catalog may exist in Postgres but not in the search index.',
      'For OpenSearch, confirm doc count at {base}:8443/{index}/_count if using a managed cluster.',
    ],
  },
  'workflow.configured': {
    to: ogSettings('settings-workflow', { workflowEngine: 'solr' }),
    label: 'OrderX → Settings → Search Workflow',
    instructions: [
      'Open the Solr tab and add at least one enabled route with a flow_name or flow_id.',
      'Save OrderX settings, then re-run readiness.',
    ],
    opensearch: {
      to: ogSettings('settings-workflow', { workflowEngine: 'opensearch' }),
      label: 'OrderX → Settings → Search Workflow (OpenSearch)',
      instructions: [
        'Open the OpenSearch tab and add at least one enabled search step/route.',
        'Save OrderX settings, then re-run readiness.',
      ],
    },
  },
  'workflow.executes': {
    to: ogSettings('settings-workflow', { workflowEngine: 'solr' }),
    label: 'OrderX → Settings → Search Workflow',
    instructions: [
      'Confirm flow names exist in System Shop flows.',
      'Enable routes and save settings, then re-run the layer 6 checks.',
    ],
    opensearch: {
      to: ogSettings('settings-workflow', { workflowEngine: 'opensearch' }),
      label: 'OrderX → Settings → Search Workflow (OpenSearch)',
      instructions: [
        'Review OpenSearch workflow steps and ensure at least one route is enabled.',
        'Save settings and re-run readiness.',
      ],
    },
  },
  'workflow.canonical_query': {
    to: ogSettings('settings-workflow', { workflowEngine: 'solr' }),
    label: 'OrderX → Settings → Search Workflow',
    instructions: [
      'Ensure the product index has data and workflow when-clauses allow the probe query.',
      'Re-index products if the catalog is empty in search.',
    ],
    opensearch: {
      to: ogSettings('settings-workflow', { workflowEngine: 'opensearch' }),
      label: 'OrderX → Settings → Search Workflow (OpenSearch)',
      instructions: [
        'Confirm OpenSearch index has products and workflow routes match your engine.',
        'Run AI Search indexing if doc count is zero.',
      ],
    },
  },
  'queue.dispatch_mode.valid': {
    to: `${ACCOUNT_ADMIN}?section=routing`,
    label: 'Account Admin → Queue Dispatch',
    instructions: [
      'Set queue_dispatch_mode to local_task (local dev) or service_bus (production).',
      'Save account admin settings.',
    ],
  },
  'queue.worker.alive': {
    to: `${ACCOUNT_ADMIN}?section=routing`,
    label: 'Account Admin → Queue Dispatch',
    instructions: [
      'For service_bus mode, start the queue worker.',
      'For local dev, prefer queue_dispatch_mode = local_task.',
    ],
  },
  'queue.concurrency.sane': {
    to: `${ACCOUNT_ADMIN}?section=capacity`,
    label: 'Account Admin → Queue Capacity',
    instructions: ['Review max concurrent requests and parallel worker limits, then save.'],
  },
  'storage.upload_dir.writable': {
    to: ogSettings('settings-defaults', { defaultsTab: 'storage' }),
    label: 'OrderX → Settings → Defaults → Storage',
    instructions: [
      'Configure a writable upload directory or Azure Blob connection string.',
      'Save OrderX settings.',
    ],
  },
  'storage.azure_blob.configured': {
    to: ogSettings('settings-defaults', { defaultsTab: 'storage' }),
    label: 'OrderX → Settings → Defaults → Storage',
    instructions: ['Enter Azure Blob connection string if blob storage is required for this deployment.'],
  },
  'notify.enabled_flags': {
    to: `${ACCOUNT_ADMIN}?section=notifications`,
    label: 'Account Admin → Notifications',
    instructions: ['Enable notifications if email/SMS alerts are required for go-live.'],
  },
  'notify.smtp.configured': {
    to: `${ACCOUNT_ADMIN}?section=notifications`,
    label: 'Account Admin → Notifications',
    instructions: ['Enter SMTP host, port, credentials, and from address; save settings.'],
  },
  'notify.twilio.configured': {
    to: `${ACCOUNT_ADMIN}?section=notifications`,
    label: 'Account Admin → Notifications',
    instructions: ['Enter Twilio Account SID, auth token, and from number if SMS is enabled.'],
  },
  'notify.test_send': {
    to: `${ACCOUNT_ADMIN}?section=notifications`,
    label: 'Account Admin → Notifications',
    instructions: ['Use Send test email/SMS on the notifications tab after saving SMTP/Twilio settings.'],
  },
  'e2e.smoke.request': {
    to: '/order-x-v2',
    label: 'OrderX V2',
    instructions: [
      'Fix upstream blockers first, then re-run full assessment with E2E smoke enabled.',
      'Use V2 to submit a test request if smoke continues to fail.',
    ],
  },
};

/**
 * @param {string | { id?: string, evidence?: { engine?: string } }} checkOrId
 * @returns {ReadinessFixGuide | null}
 */
export function getReadinessFixGuide(checkOrId) {
  const check = typeof checkOrId === 'string' ? { id: checkOrId } : checkOrId;
  const checkId = String(check?.id || '').trim();
  if (!checkId) return null;

  const entry = FIX_BY_CHECK_ID[checkId];
  if (!entry) return null;

  const engine = String(check?.evidence?.engine || '').toLowerCase();
  const engineOverride = engine === 'opensearch' ? entry.opensearch : null;

  return {
    to: engineOverride?.to || entry.to,
    label: engineOverride?.label || entry.label,
    instructions: engineOverride?.instructions || entry.instructions,
  };
}

export function shouldShowReadinessFixGuide(check) {
  const status = String(check?.status || '').toLowerCase();
  if (check?.evidence?.using_defaults === true) return false;
  return ['fail', 'error', 'running'].includes(status);
}
